import { CloseButton, SaveButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { userProvider } from '../../../data/providers/userProvider';
import { IOrganizationOptions, IUser } from '../../../data/types';
import { saveOrgOptions } from '../../../data/writers/settings/orgOptions';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { FormContainer } from '../../shared/form/FormContainer';
import { Toggle } from '../../shared/form/Toggle';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';

interface IProps {
  onClose: () => void;
}

export const OrgOptionsPopup = (props: IProps): React.ReactElement => {
  const [orgOptions, setOrgOptions] = React.useState<IOrganizationOptions>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState<boolean>(false);

  /**
   * @description subscribe to the current org options
   */
  React.useEffect((): (() => void) => {
    const userSubscription = userProvider().subscribe((user: IUser): void => {
      setOrgOptions(user.orgOptions);
      setLoading(false);
    });
    return (): void => userSubscription.unsubscribe();
  }, []);

  /**
   * @description handle the options change
   * @param value
   */
  const onChange = (option: string, value: boolean): void => {
    setOrgOptions({
      ...(orgOptions as IOrganizationOptions),
      [option]: value,
    });
  };

  /**
   * @description handle the form submission
   */
  const onSubmit = async (): Promise<void> => {
    setSaving(true);
    await saveOrgOptions(orgOptions);
    props.onClose();
  };

  return (
    <PopupCard onClose={props.onClose} noClickOutside size='small'>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Organization Options</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <FormContainer>
            {!loading ? (
              <React.Fragment>
                <Toggle
                  name='enforceUniqueAthleteId'
                  onChange={onChange}
                  label='Require Athlete Unique IDs'
                  checked={orgOptions?.enforceUniqueAthleteId}
                  value='enforceUniqueAthleteId'
                />
                <InfoBox>
                  <p>
                    When enabled, Unique ID becomes a required field when
                    creating new athletes. Requiring Athlete Unique IDs can help
                    to ensure their consistent use across an organization. We
                    recommend enabling this option only if Unique IDs are
                    consistently known at the time of testing.
                  </p>
                </InfoBox>
              </React.Fragment>
            ) : null}
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton disabled={saving} onClick={onSubmit} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

const InfoBox = styled.div`
  margin-top: 15px;
  border-radius: 5px;
  border: 1px solid var(--color-accent);
  padding: 10px;
  display: flex;
`;
